import { AfterViewInit, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { DialogService, eDialogType } from '@ds/ng-wt-dialog';
import { IMenu } from '@ds/ng-wt-layout';
import { filter } from 'rxjs/operators';
import { AppSettingsService } from '@ds/ng-wt-app-settings';
import { LoginDetailsService } from '../login/services/login-details.service';
import { MenusComponent } from 'src/app/shared/components/menus/menus.component';
import { PlayerService } from 'src/app/shared/services/player.service';
import { Subscription } from 'rxjs';
import { AuthService } from '../authentication/auth.service';
import { IdleTimeoutInitializer } from '../authentication/idle-timeout-initializer.service';
import { CONTENT_ROUTES_PATHS } from 'src/app/shared/constants/contents.constants';
import { ConfirmationAlertDialogComponent } from '../login/components/confirmation-alert-dialog/confirmation-alert-dialog.component';
import { NgToastTranslocoService } from 'src/app/common/services/ng-toast-transloco/ng-toast-transloco.service';
 
@Component({
  selector: 'ds-shell',
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss'],
})
export class ShellComponent implements OnInit, AfterViewInit, OnDestroy {
  menus: any = [];
  isSetting = false;
  displayChildMenu: boolean = false;
  contactusInformation:any;
  downloadAppInformation: any;
  menuPanelCollapsed: boolean = false;
  applicationConfigs: any = (window as any)['config'];
  headerImage: string = '';
  moreMenus: any | [] = [];
  mobileMenuSubscription: Subscription | undefined;
  privacyPolicyUrl: string = '';
  termsUrl: string = '';
  helpUrl: string = '';
  accessibilityUrl: string = '';
  selectedSectionColor: any;
  unselectedSectionColor: any;
 
  constructor(
    public route: Router,
    public dialogService: DialogService,
    public appSettingService: AppSettingsService,
    private loginDetailsService: LoginDetailsService,
    private changeDetectorRef: ChangeDetectorRef,
    private playerService: PlayerService,
    public translocoService: NgToastTranslocoService,
    private authService: AuthService,
    private idleTimeoutInitializer: IdleTimeoutInitializer,
    private toast: NgToastTranslocoService
  ) {
    this.contactusInformation = this.applicationConfigs.contact_us;
    this.downloadAppInformation = this.applicationConfigs.download_app_urls;
    this.menus = this.loginDetailsService.menuItems;
    this.moreMenus = this.loginDetailsService.moreMenuItems;
    this.headerImage = this.loginDetailsService?.headerImage ? this.loginDetailsService.headerImage : '';
    this.privacyPolicyUrl = this.loginDetailsService.privacyPolicyUrl || '';
    this.termsUrl = this.loginDetailsService.termsUrl || '';
    this.helpUrl = this.loginDetailsService.helpUrl || '';
    this.accessibilityUrl = this.loginDetailsService.accessibilityUrl || '';
    this.selectedSectionColor = this.loginDetailsService.selectedSectionColor || '';
    this.unselectedSectionColor = this.loginDetailsService.unselectedSectionColor || '';
 
    this.mobileMenuSubscription = this.playerService.mobileMenuUpdate.subscribe((value: boolean) => {
      if(value) {
        this.unsetMenuStyles(this.menus);
      }
    });
 
  }
 
  ngOnInit() {
    console.log('[ShellComponent] Initializing shell component');
    this.handleSettings();

    if (this.route.url.includes('settings')) {
      this.isSetting = true;
    }

    // Re-initialize idle timeout on each login
    // This ensures the idle timeout system is active after user authentication
    console.log('[ShellComponent] Re-initializing idle timeout system');
    this.idleTimeoutInitializer.initialize();
    this.route.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe((event: NavigationStart) => {
        this.processSectionToSetActive(event.url);
      });
  }
 
  ngAfterViewInit(): void {
    this.processSectionToSetActive(this.route.url);
  }
 
   processSectionToSetActive(routeUrl: string) {
    if(this.isBeingNavigatedToOffers(routeUrl) && this.isOffersSectionConfigured()) {
      const routeArray = routeUrl.split('/');
      const menuToHighlight = '/' + routeArray[1] + '/' + routeArray[3];
      this.setActiveMenu(menuToHighlight);
    } else {
      this.setActiveMenu(routeUrl);
    }
  }
 
  private isOffersSectionConfigured() {
    return this.menus && this.menus.some((item: any) => item?.contentType?.toLowerCase() === 'offers');
  }
 
  private isBeingNavigatedToOffers(url: string): boolean {
    return !!(url && url.toLowerCase().startsWith('/offers/'));
  }
 
  setActiveMenu(routerUrl: string): void {
    // check for child menu items
    const index = routerUrl.indexOf('/', 1);
 
    // get the parent route name from url
    let routeName = routerUrl;
    if(index !== -1) {
      routeName = routerUrl.split('/')[2];
      this.checkForChildMenuDisplay(routeName);
    } else {
      this.displayChildMenu = true;
    }
    this.markActiveMenu(routeName);
    this.changeDetectorRef.detectChanges();
  }
 
  markActiveMenu(currentUrl: string) {
    this.unsetMenuStyles(this.menus);
    const flatMenus = this.flattenMenus(this.menus);
 
    currentUrl = decodeURIComponent(currentUrl);
    const matched = flatMenus.find(menu => (menu?.routerUrl && menu.routerUrl.includes(currentUrl)));
    if (matched) {
      matched.active = true;
      const el = document.getElementById(matched.name);
      if (el) {
        el.setAttribute('class', 'active');
      }
    }
 
    this.authService.startRefreshTimerIfTokenInvalid();
  }
 
  flattenMenus(menus: IMenu[]): any[] {
    let result: any[] = [];
    for (const menu of menus) {
      result.push(menu);
      if (menu.children && menu.children.length) {
        result = result.concat(this.flattenMenus(menu.children));
      }
    }
    return result;
  }
 
  handleSettings() {
    this.route.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.isSetting = event.url.includes('settings');
      });
  }
 
  onMenuClick(selectedMenu: any) {
    if (selectedMenu.name === this.translocoService.gettransloco('MENU.LOGOUT')) {
      this.logout();
      return;
    }
    this.unsetMenuStyles(this.menus);
    if(selectedMenu.children && selectedMenu.children.length > 0) {
      this.displayChildMenu = !this.displayChildMenu;
      selectedMenu.active = true;
      const el = document.getElementById(selectedMenu.name);
      if (el) el.setAttribute('class', 'active');
      window && window.innerWidth <= 1023 ? this.goToProfileMenu(selectedMenu) : '';
    } else {
      selectedMenu.active = true;
      const element = document.getElementById(selectedMenu.name);
      if (element) {
        element.setAttribute('class', 'active');
      }
      this.route.navigateByUrl(selectedMenu.routerUrl, { state: { key: selectedMenu.categoryKey }});
    }
    this.playerService.moreMenuUpdate.next(true);
  }

  logout() {
    this.dialogService
      .open(ConfirmationAlertDialogComponent, {
        data: {
          dialogTitle: this.toast.gettransloco('MODULES.COMMON.MESSAGES.CONFIRMATION_HEADER'),
          dialogMessage: this.toast.gettransloco('MODULES.COMMON.MESSAGES.CONFIRMATION_LOGOUT_MESSAGE'),
        },
        width: '344px',
        closeButton: false,
      })
      .afterClosed$.subscribe((result: string) => {
        if (result === 'Ok') {
          // Cleanup idle timeout system before logout
          this.idleTimeoutInitializer.cleanup();
          this.authService.logoutSignedInUser();
        }
      });
  }
 
  unsetMenuStyles(menuList: IMenu[]) {
    menuList.forEach((menuItem) => {
      if (menuItem.active) {
        menuItem.active = false;
        document.getElementById(menuItem.name)?.removeAttribute('class');
        return;
      }
      if(menuItem.children && menuItem.children.length > 0){
        this.unsetMenuStyles(menuItem.children);
      }
    });
  }
 
  @HostListener('window:scroll')
  addScrollButton(){
    const scrollIcon = document.getElementsByClassName('vjScrollUp')[0];
    if(scrollIcon){
      const scrollTopValue = document.documentElement.scrollTop || window.pageYOffset;
      if(scrollTopValue > 20){
        if(!scrollIcon.classList.contains('active')) scrollIcon.classList.add('active')
      }
      else{
        if(scrollIcon.classList.contains('active')) scrollIcon.classList.remove('active')
      }
    }
  }
 
  scrollToTop(){
    window.scrollTo({top: 0, behavior: 'smooth'});
  }
 
  goToProfileMenu(selectedMenu: any) {
    this.route.navigateByUrl(selectedMenu.routerUrl);
  }
 
  openMobileViewMenus(){
    this.dialogService.open(MenusComponent, {
      windowClass: 'vjDialogFlyout',
      width: '280px',
      enableClose: true,
      type: eDialogType.Flyout,
      data: {
        menus: this.moreMenus
      }
    })
  }
 
  ngOnDestroy(): void {
    this.mobileMenuSubscription?.unsubscribe();
  }
 
  checkForChildMenuDisplay(routeName: string) {
    if(routeName === CONTENT_ROUTES_PATHS.ACCOUNT_SECURITY) {
      this.displayChildMenu = true;
    } else {
      this.displayChildMenu = false;
    }
  }

  openPrivacyPolicy() {
    if (this.privacyPolicyUrl) {
      window.open(this.privacyPolicyUrl, '_blank');
    }
  }

  openTermsAndConditions() {
    if (this.termsUrl) {
      window.open(this.termsUrl, '_blank');
    }
  }

  openAccessibility() {
    if (this.accessibilityUrl) {
      window.open(this.accessibilityUrl, '_blank');
    }
  }

  openHelp() {
    if (this.helpUrl) {
      window.open(this.helpUrl, '_blank');
    }
  }
}